import debounce from 'lodash/debounce';
import Socket from 'services/Socket';
import getMoment from 'utils/getMoment';
import store from 'store';
import { bindActionCreators } from 'redux';
import auth from 'store/app/auth/action';
import bulkMessagings from 'store/app/entities/bulkMessaging/action';
import channels from 'store/app/entities/channels/action';
import companies from 'store/app/entities/companies/action';
import employeeImports from 'store/app/entities/employeeImports/action';
import employees from 'store/app/entities/employees/action';
import messages from 'store/app/entities/messages/action';
import users from 'store/app/entities/users/action';
import visibility from 'store/app/ui/focus/action';
import authCompany from 'store/selectors/authCompany';
import authEmployee from 'store/selectors/authEmployee';
import populateChannels from 'store/selectors/populateChannels';
import {
  findEmployeeInApiCaches,
  patchEmployeeInApiCaches,
} from 'store/app/entities/EmployeesApiSlice';
import { resolvePreservedAvatar } from 'utils/preserveSignedAvatar';
import { refreshPendingSignatureCount } from 'components/@home/@signatures/pendingSignatures';

// Fire immediately and every 2s while typing
const debounceTypingActive = debounce(
  (actions, channel, user, status) => {
    actions.users.typing(channel, user, status);
  },
  2000,
  { leading: true, maxWait: 2000, trailing: false },
);

// Fire after 2,5s of inactivity
const debounceTypingInactive = debounce(
  (actions, channel, user, status) => {
    actions.users.typing(channel, user, status);
  },
  2500,
  { leading: false, trailing: true },
);

const registerSocketListeners = () => {
  const state = store.getState();
  const { dispatch } = store;
  const props = {
    actions: {
      visibility: bindActionCreators(visibility, dispatch),
      auth: bindActionCreators(auth, dispatch),
      users: bindActionCreators(users, dispatch),
      employeeImports: bindActionCreators(employeeImports, dispatch),
      employees: bindActionCreators(employees, dispatch),
      companies: bindActionCreators(companies, dispatch),
      messages: bindActionCreators(messages, dispatch),
      channels: bindActionCreators(channels, dispatch),
      bulkMessagings: bindActionCreators(bulkMessagings, dispatch),
    },
    channels: populateChannels(state),
    authEmployee: authEmployee(state),
    users: state.users,
    authCompany: authCompany(state),
  };

  const isSignatureMessage = message =>
    Boolean(message?.isMandatory || message?.digitalSignature || message?.signatureKind);

  const fixOneToOneChannelId = rawMessage => {
    if (rawMessage.channel === props.authEmployee?.user?._id) {
      return rawMessage.from._id;
    }
    return rawMessage.channel;
  };

  Socket.on('connect', () => {
    props.actions.visibility.onConnect();
  });

  Socket.on('newMessage', ({ message: rawMessage }) => {
    const fixedRawMessage = {
      ...rawMessage,
      channel: fixOneToOneChannelId(rawMessage),
      createdAt: getMoment(rawMessage.createdAt).toISOString(),
      updatedAt: getMoment(rawMessage.updatedAt).toISOString(),
    };
    // If we receive a message in the current channel open, we should mark it as read
    // only if the document is visible (hence `maybe` it will be mark as read)
    props.actions.messages.addAndMaybeRead(fixedRawMessage);
    if (isSignatureMessage(fixedRawMessage)) refreshPendingSignatureCount();
  });

  // Voice agent word-by-word streaming: grow the text of an existing message in place.
  Socket.on('messageStream', ({ message: rawMessage }) => {
    if (!rawMessage?._id) return;
    const channelId = fixOneToOneChannelId(rawMessage);
    props.actions.messages.streamText({
      _id: rawMessage._id,
      text: rawMessage.text,
      channelId,
      message: { ...rawMessage, channel: channelId },
    });
  });

  Socket.on('bulkMessagingSent', ({ bulkMessaging, lastSent }) => {
    props.actions.bulkMessagings.add({
      ...bulkMessaging,
      lastSent,
    });
  });

  Socket.on('bulkMessagingError', ({ bulkMessaging, lastSent }) => {
    props.actions.bulkMessagings.add({
      ...bulkMessaging,
      lastSent,
    });
  });

  Socket.on('userOnline', ({ user }) => {
    props.actions.users.set(user);
  });

  Socket.on('userOffline', ({ user }) => {
    props.actions.users.set(user);
  });

  Socket.on('typing', ({ channel, status, user }) => {
    if (status) {
      debounceTypingActive(props.actions, channel, user, true);
      debounceTypingInactive(props.actions, channel, user, false);
    } else {
      props.actions.users.typing(channel, user, false);
    }
  });

  Socket.on('newEmployee', ({ employee }) => {
    props.actions.employees.add(employee._id, employee);
  });

  Socket.on('employeeUpdated', async ({ employee }) => {
    const existing =
      store.getState().employees?.employees?.[employee._id] ||
      findEmployeeInApiCaches(store.getState, employee._id);
    const userId = employee.user?._id || employee.user;
    const dmAvatar = userId ? populateChannels(store.getState())?.[userId]?.avatar : undefined;
    const patch = { ...employee };
    const preserved = resolvePreservedAvatar({
      incomingAvatar: employee.avatar,
      existingAvatar: existing?.avatar,
      fallbackAvatar: dmAvatar,
    });
    if (preserved != null) {
      patch.avatar = preserved;
    }
    props.actions.employees.modify(employee._id, patch);
    patchEmployeeInApiCaches(store.dispatch, store.getState, employee._id, patch);
  });

  Socket.on(['newCompany', 'employeeUnblocked', 'companyUnblocked'], ({ company }) => {
    props.actions.companies.set(company);
  });

  Socket.on(['companyBlocked'], async data => {
    props.actions.companies.remove(data.company._id);

    if (props.authCompany._id === data.company._id) {
      props.actions.auth.logout();
    }
  });

  Socket.on(['userBlocked', 'userDeleted'], data => {
    if (props.authEmployee.user_id === data.user._id) {
      props.actions.auth.logout();
    }
  });

  Socket.on(['employeeDeleted', 'employeeBlocked'], async data => {
    if (data.employee.user._id === props.authEmployee.user_id) {
      props.actions.companies.remove(data.company._id);
    }

    if (props.authEmployee._id === data.employee._id) {
      props.actions.auth.logout();
    }
  });

  Socket.on('companyDeleted', async data => {
    props.actions.companies.remove(data.company._id);
    if (props.authCompany._id === data.company._id) {
      props.actions.auth.logout();
    }
  });

  Socket.on('companyUpdated', ({ company }) => {
    if (company?._id && props.authCompany._id === company._id) {
      props.actions.companies.set(company);
    }
  });

  Socket.on(['newChannel', 'channelUpdated'], ({ channel }) => {
    if (channel._id !== props.authEmployee.user._id) {
      const existing = populateChannels(store.getState())?.[channel._id];
      const preserved = resolvePreservedAvatar({
        incomingAvatar: channel.avatar,
        existingAvatar: existing?.avatar,
      });
      props.actions.channels.set({
        ...channel,
        ...(preserved != null ? { avatar: preserved } : {}),
        isMember: Object.prototype.hasOwnProperty.call(channel, 'isMember')
          ? channel.isMember
          : true,
      });
    }
  });

  Socket.on(['channelClosed'], ({ channel }) => {
    if (channel._id !== props.authEmployee.user._id) {
      // forceLeave: do not preserve prior list membership (OMM-699)
      props.actions.channels.set({ ...channel, isMember: false, forceLeave: true });
    }
  });

  Socket.on('joinChannel', ({ user, channel, status }) => {
    const channelId = props.channels[channel]?._id || user?._id;

    if (status && props.authEmployee?.user_id !== user?._id) {
      props.actions.channels.watch(channelId);
      props.actions.messages.markAsRead(channelId);
    }
  });

  Socket.on('deleteMessage', ({ message }) => {
    props.actions.messages.destroyFulfilled(message);
    if (isSignatureMessage(message)) refreshPendingSignatureCount();
  });

  Socket.on('readMessage', payload => {
    props.actions.messages.onReadMessage(payload);
  });

  Socket.on('checkSession', () => {
    props.actions.auth.verify();
  });

  Socket.on('importFinished', payload => {
    props.actions.employeeImports.finished(payload);
  });

  Socket.on('importDryRunProgress', payload => {
    const data =
      Array.isArray(payload) && payload[1] && typeof payload[1] === 'object' ? payload[1] : payload;
    const currentImportId = store.getState()?.drawers?.employeeImports?.currentImportId;
    const eventImportId = data?.importId != null ? String(data.importId) : null;
    if (currentImportId && eventImportId && String(currentImportId) === eventImportId) {
      props.actions.employees.importDryRunProgress(data);
    }
  });

  Socket.on('importDryRunFinished', payload => {
    const data =
      Array.isArray(payload) && payload[1] && typeof payload[1] === 'object' ? payload[1] : payload;
    const currentImportId = store.getState()?.drawers?.employeeImports?.currentImportId;
    const eventImportId = data?.employeeImport?._id ?? data?.employeeImportId ?? data?.importId;
    const eventImportIdStr = eventImportId != null ? String(eventImportId) : null;
    if (currentImportId && eventImportIdStr && String(currentImportId) === eventImportIdStr) {
      props.actions.employees.importDryRunFinished(data);
    }
  });
};

const initializeSockets = () => {
  if (Socket.initialized()) {
    return Socket;
  }
  Socket.setListenerSetup(registerSocketListeners);
  Socket.connect();
  return Socket;
};

export default initializeSockets;
